import { useCallback, useState } from "react";
import { KeyRound } from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import { Button } from "@/components/ui/button";
import Authorized from "components/Authorized";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import ConfirmDialog from "components/modals/ConfirmDialog";
import ApiKeyService from "services/ApiKey";
import ApiKeyModal from "routes/admin/components/ApiKeyModal";
import RawKeyRevealModal from "routes/admin/components/RawKeyRevealModal";
import {
  invalidateApiKeysQuery,
  useApiKeyMutations,
  useApiKeysTable,
} from "hooks/useApiKeysTable";

export const API_KEYS_TABLE_CONSTANTS = {
  tableName: "api_keys_table",
  queryKey: "api-keys-table",
  className: "api_keys_table",
  exportTableName: null,
  header: "API Keys",
};

const defaultFilters = {
  status: ["active"],
};

// TableTop renders a string headerIcon through Semantic's Icon; passing a node
// keeps this page free of Semantic UI.
const headerIcon = (
  <span
    className="tw:inline-flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary tw:text-primary-foreground"
    aria-hidden="true"
  >
    <KeyRound className="tw:size-4" />
  </span>
);

const toRevealedKey = response => ({
  rawKey: response.raw_key,
  serviceName: response.api_key?.service_name,
  scopes: response.api_key?.scopes || [],
});

const ApiKeysTableV3 = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [keyToRotate, setKeyToRotate] = useState(null);
  const [keyToRevoke, setKeyToRevoke] = useState(null);

  const { rotate, revoke } = useApiKeyMutations({
    onRotated: response => {
      setKeyToRotate(null);
      setRevealedKey(toRevealedKey(response));
    },
    onRevoked: () => setKeyToRevoke(null),
  });

  const handleCreated = response => {
    setIsCreateOpen(false);
    setRevealedKey(toRevealedKey(response));
  };

  const { columns, filters } = useApiKeysTable({
    onRotate: setKeyToRotate,
    onRevoke: setKeyToRevoke,
  });

  const fetchApiKeys = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);
        return await ApiKeyService.list(nextQueryParams);
      } catch (error) {
        console.error("Error fetching api keys:", error);
        throw error;
      }
    },
    [columns]
  );

  return (
    <>
      <ApiKeyModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={handleCreated}
      />
      <RawKeyRevealModal
        revealedKey={revealedKey}
        onClose={() => setRevealedKey(null)}
      />
      <ConfirmDialog
        open={!!keyToRotate}
        onOpenChange={open => !open && setKeyToRotate(null)}
        title="Rotate API Key"
        description={
          <>
            This will revoke the existing key for{" "}
            <strong>{keyToRotate?.service_name}</strong> and generate a new one.
            Continue?
          </>
        }
        confirmText="Rotate"
        onConfirm={() => !rotate.isPending && rotate.mutate(keyToRotate.id)}
      />
      <ConfirmDialog
        open={!!keyToRevoke}
        onOpenChange={open => !open && setKeyToRevoke(null)}
        title="Revoke API Key"
        description={
          <>
            Are you sure you want to revoke the key for{" "}
            <strong>{keyToRevoke?.service_name}</strong>? This cannot be undone.
          </>
        }
        confirmText="Revoke"
        confirmVariant="destructive"
        onConfirm={() => !revoke.isPending && revoke.mutate(keyToRevoke.id)}
      />
      <BaseTableV3
        columns={columns}
        filters={filters}
        defaultFilters={defaultFilters}
        queryKey={API_KEYS_TABLE_CONSTANTS.queryKey}
        className={API_KEYS_TABLE_CONSTANTS.className}
        tableName={API_KEYS_TABLE_CONSTANTS.tableName}
        header={API_KEYS_TABLE_CONSTANTS.header}
        headerIcon={headerIcon}
        createButton={
          <Authorized permissions={[ACL_RELATIONSHIPS.adminApiKeys.create]}>
            <Button size="sm" onClick={() => setIsCreateOpen(true)}>
              New API Key
            </Button>
          </Authorized>
        }
        fetchData={fetchApiKeys}
        invalidateData={invalidateApiKeysQuery}
      />
    </>
  );
};

export default ApiKeysTableV3;
